import { getEntityColor, getEntityName, getTypeIcon } from "../utils";

import { NewItemEntityType, NewItemTypeConfig } from "./new-item-types";

import LoadingSpinner from "@/components/LoadingSpinner";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import { EntityType } from "@/queries/folder-entities/types";

interface NewItemPopoverContentProps {
  groups: NewItemTypeConfig[][];
  onItemClick: (type: NewItemEntityType) => void;
  isCreateDisabled?: (type: EntityType) => boolean;
  isLoading?: boolean;
  loadingType?: EntityType | null;
}

interface ItemRowProps {
  item: NewItemTypeConfig;
  onItemClick: (type: NewItemEntityType) => void;
  isDisabled: boolean;
  isLoading: boolean;
  isPermissionDisabled: boolean;
}

const ItemRow = ({
  item,
  onItemClick,
  isDisabled,
  isLoading,
  isPermissionDisabled,
}: ItemRowProps) => {
  const icon = getTypeIcon(item.type);
  const backgroundColor = isLoading ? "bg-transparent" : getEntityColor(item.type);
  const label = getEntityName(item.type);

  const button = (
    <Button
      disabled={isDisabled}
      variant="ghost"
      className="group relative flex h-auto w-full items-center justify-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-100/80 disabled:cursor-not-allowed disabled:opacity-40"
      onClick={() => onItemClick(item.type)}
    >
      <div
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${backgroundColor} transition-transform group-hover:scale-105`}
      >
        {isLoading ? <LoadingSpinner size={4} className="!mr-0" /> : icon}
      </div>
      <div className="flex min-w-0 flex-col items-start">
        <span className="text-sm font-medium text-gray-900">
          {isLoading ? `Creating ${label.toLowerCase()}...` : label}
        </span>
        <span className="truncate text-xs text-gray-500">
          {isLoading ? "This may take a few seconds." : item.description}
        </span>
      </div>
      {!isLoading && item.badge ? (
        <span className="absolute right-3 top-2.5 rounded-full border border-blue-100 bg-blue-50/70 px-1.5 py-px text-[10px] font-medium leading-4 text-blue-600">
          {item.badge}
        </span>
      ) : null}
    </Button>
  );

  if (isPermissionDisabled) {
    return (
      <BasicTooltip content="You do not have permission to create this item" shouldShow>
        <span className="flex w-full">{button}</span>
      </BasicTooltip>
    );
  }

  return button;
};

const NewItemPopoverContent = ({
  groups,
  onItemClick,
  isCreateDisabled,
  isLoading = false,
  loadingType = null,
}: NewItemPopoverContentProps) => (
  <div className="flex flex-col">
    {groups.map((group, groupIndex) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={`group-${groupIndex}`}>
        {groupIndex > 0 && <div className="mx-2 my-1 border-t border-gray-200" />}
        {group.map(item => (
          <ItemRow
            key={item.type}
            item={item}
            onItemClick={onItemClick}
            isDisabled={isLoading || !!isCreateDisabled?.(item.type)}
            isLoading={loadingType === item.type}
            isPermissionDisabled={!!isCreateDisabled?.(item.type)}
          />
        ))}
      </div>
    ))}
  </div>
);

export default NewItemPopoverContent;
